import React from "react";
import Icon from "./icons";
import css from "./Scheduler.module.css";
import {
  DOW,
  addDays,
  sameDay,
  startOfDay,
  fmtRange,
  fmtTime,
  clustersOf,
  entryMeta,
  CAT_COLOR,
  shiftDiscrepancy,
  DISCREPANCY_LABEL,
} from "./scheduleUtils";

// Small at-a-glance badge for a shift's attendance discrepancy (late clock-in
// or missed shift), shown on the calendar card so admins don't have to open
// each shift to spot a problem.
export const ExcBadge = ({ exc }) =>
  exc ? (
    <span
      title={DISCREPANCY_LABEL[exc]}
      className={css.evExc}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 3,
        fontSize: 9.5,
        fontWeight: 800,
        letterSpacing: "0.02em",
        textTransform: "uppercase",
        padding: "1px 5px",
        borderRadius: 999,
        whiteSpace: "nowrap",
        background: exc === "late" ? "#fef3c7" : "#fee2e2",
        color: exc === "late" ? "#b45309" : "#b42318",
      }}
    >
      <Icon name="alert" size={9} /> {exc === "late" ? "Late" : "Missed"}
    </span>
  ) : null;

const HOUR_H = 52;

// Time-off type short labels + which days a request covers (compared by
// calendar day so it stays aligned regardless of timezone).
const OFF_LABEL = { pto: "PTO", vacation: "Vacation", sick: "Sick", unpaid: "Unpaid", other: "Off" };
const ymdLocal = (d) => { const x = new Date(d); return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`; };
const ymdUTC = (d) => { const x = new Date(d); return `${x.getUTCFullYear()}-${String(x.getUTCMonth() + 1).padStart(2, "0")}-${String(x.getUTCDate()).padStart(2, "0")}`; };
export const offForDay = (timeOff, d) => (timeOff || []).filter((t) => {
  const day = ymdLocal(d);
  // Explicit day list (may be non-continuous) takes precedence; older records
  // without it fall back to the start/end envelope.
  if (Array.isArray(t.dates) && t.dates.length) return t.dates.some((dd) => ymdUTC(dd) === day);
  return day >= ymdUTC(t.startDate) && day <= ymdUTC(t.endDate);
});
// One compact time-off indicator per day. A single request shows the person +
// type; multiple collapse to "N off" so the header never floods when several
// staff are out. Amber (dashed) if any are still pending, green if all approved.
// The tooltip lists everyone.
export const OffSummary = ({ items }) => {
  const list = items || [];
  if (!list.length) return null;
  const anyPending = list.some((t) => t.status === "pending");
  // A single, general indicator with an icon — never names the person (details
  // live in the tooltip and the Time Off list). Scales cleanly to many staff.
  const label = `${list.length} time-off request${list.length === 1 ? "" : "s"}`;
  const title = list
    .map((t) => `${t.staff?.name || "Staff"} · ${OFF_LABEL[t.type] || "Time off"}${t.status === "pending" ? " (pending)" : " (approved)"}`)
    .join("\n");
  return (
    <div
      title={title}
      style={{
        display: "inline-flex", alignItems: "center", gap: 4,
        fontSize: 10, fontWeight: 700, borderRadius: 6, padding: "2px 6px", marginTop: 3,
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%",
        background: anyPending ? "#fef3c7" : "#dbeafe",
        color: anyPending ? "#b45309" : "#1e40af",
        border: anyPending ? "1px dashed #fcd34d" : "1px solid #bfdbfe",
      }}
    >
      <Icon name="boat" size={11} style={{ flexShrink: 0 }} />
      <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{label}</span>
    </div>
  );
};

// A time-off calendar item (sits in a day cell like a shift card): staff name +
// type and approval status, colour-coded amber/dashed for pending, green for
// approved. Clickable when an onClick is provided.
const OFF_STATUS_LABEL = { pending: "Pending", approved: "Approved", denied: "Denied", cancelled: "Cancelled" };
export const TimeOffCard = ({ item, onClick }) => {
  if (!item) return null;
  const pending = item.status === "pending";
  const approved = item.status === "approved";
  const type = OFF_LABEL[item.type] || "Time off";
  return (
    <button
      type="button"
      onClick={onClick ? () => onClick(item) : undefined}
      title={`${item.staff?.name || "Staff"} · ${type} (${OFF_STATUS_LABEL[item.status] || item.status})`}
      style={{
        display: "flex", flexDirection: "column", gap: 2, width: "100%", textAlign: "left",
        border: pending ? "1px dashed #fcd34d" : approved ? "1px solid #bfdbfe" : "1px solid var(--app-border)",
        background: pending ? "#fef3c7" : approved ? "#dbeafe" : "var(--app-surface-2)",
        borderRadius: 8, padding: "5px 8px", cursor: onClick ? "pointer" : "default", font: "inherit",
      }}
    >
      <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700, letterSpacing: "0.01em", color: pending ? "#b45309" : approved ? "#1e40af" : "var(--app-text)" }}>
        <Icon name="boat" size={12} style={{ flexShrink: 0 }} />
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Time-off request</span>
      </span>
      <span style={{ fontSize: 10.5, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: pending ? "#b45309" : approved ? "#1e40af" : "var(--app-text)" }}>
        {item.staff?.name || "Staff"}
      </span>
      <span style={{ fontSize: 10, fontWeight: 600, color: pending ? "#b45309" : approved ? "#1e40af" : "var(--app-text-muted)" }}>
        {type} · {OFF_STATUS_LABEL[item.status] || item.status}
      </span>
    </button>
  );
};

export default function WeekCalendar({ shifts = [], timeOff = [], weekStart, onShiftClick, onHover, onLeave, windowStart = 6 * 60, windowEnd = 21 * 60, dropArmed = false, onDayDrop, onShiftMove }) {
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const now = new Date();
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const [dropDay, setDropDay] = React.useState(null);
  // id of an existing shift currently being dragged to a new day
  const [movingId, setMovingId] = React.useState(null);

  // normalize shift dates; the grid spans the facility operating window
  // (windowStart..windowEnd, default 6 AM–9 PM), expanding to fit any shift
  // that falls outside it.
  const items = shifts.map((s) => ({ ...s, _date: startOfDay(new Date(s.date)) }));
  let sMin = windowStart;
  let eMin = windowEnd;
  items.forEach((s) => {
    if (s.allDay) return;
    sMin = Math.min(sMin, Math.floor(s.startMin / 60) * 60);
    eMin = Math.max(eMin, Math.ceil(s.endMin / 60) * 60);
  });
  const trackH = ((eMin - sMin) / 60) * HOUR_H;

  const heads = days.map((d) => {
    const isToday = sameDay(d, now);
    const count = items.filter((s) => sameDay(s._date, d)).length;
    return (
      <div key={d.toISOString()} className={`${css.head} ${isToday ? css.today : ""}`}>
        <div className={css.dow}>{DOW[d.getDay()]}</div>
        <div className={css.dnum}>{d.getDate()}</div>
        <div className={css.cnt}>{count ? `${count} shift${count > 1 ? "s" : ""}` : "-"}</div>
        <OffSummary items={offForDay(timeOff, d)} />
      </div>
    );
  });

  const renderAv = (meta, color) => {
    if (meta.staffPhoto) {
      return (
        <span className={css.evAv} style={{ background: color.line, boxShadow: `0 0 0 1.5px ${color.line}` }}>
          <img src={meta.staffPhoto} alt="" />
        </span>
      );
    }
    if (meta.staffAv) {
      return (
        <span className={css.evAv} style={{ background: color.line, boxShadow: `0 0 0 1.5px ${color.line}` }}>
          {meta.staffAv}
        </span>
      );
    }
    return (
      <span className={`${css.evAv} ${css.evAvNone}`}>
        <Icon name="user" size={14} />
      </span>
    );
  };

  const hours = [];
  for (let m = sMin; m < eMin; m += 60) {
    hours.push(
      <div key={m} className={css.hr} style={{ height: HOUR_H }}>
        <span>{fmtTime(m).replace(":00", "")}</span>
      </div>
    );
  }

  const tracks = days.map((d, di) => {
    const isToday = sameDay(d, now);
    const isWeekend = d.getDay() === 0 || d.getDay() === 6;
    const dayShifts = items.filter((s) => sameDay(s._date, d)).sort((a, b) => a.startMin - b.startMin);
    const lines = [];
    for (let m = sMin; m < eMin; m += 60) {
      lines.push(<div key={m} className={css.line} style={{ top: ((m - sMin) / 60) * HOUR_H }} />);
    }
    const events = [];
    clustersOf(dayShifts).forEach((cl) => {
      const step = cl.ncol > 1 ? Math.min(16, 60 / (cl.ncol - 1)) : 0;
      cl.items.forEach(({ s, col }) => {
        const meta = entryMeta(s);
        // Colour by kind: appointments green, shifts purple, open amber.
        const colorCat = meta.cat === "open" ? "open" : meta.appt ? "facility" : "client";
        const color = CAT_COLOR[colorCat] || CAT_COLOR.client;
        const exc = shiftDiscrepancy(s);
        const top = ((s.startMin - sMin) / 60) * HOUR_H;
        const h = Math.max(64, ((s.endMin - s.startMin) / 60) * HOUR_H - 3);
        const left = col * step;
        const isPast =
          s._date < startOfDay(now) || (sameDay(s._date, now) && s.endMin <= nowMin);
        const isOpen = meta.cat === "open";
        const headStyle = meta.appt
          ? { background: "var(--app-surface)", color: color.ink, boxShadow: `inset 0 -1px 0 ${color.line}` }
          : { background: color.line };
        events.push(
          <button
            key={s._id || s.id}
            className={`${css.ev} ${isOpen ? css.open : ""} ${isPast ? css.done : ""}`}
            style={{ top, height: h, left: `calc(${left}% + 1px)`, width: `calc(${100 - left}% - 2px)`, borderColor: color.line, zIndex: col + 1 }}
            onClick={() => onShiftClick?.(s)}
            onMouseEnter={(e) => onHover?.(s, e)}
            onMouseMove={(e) => onHover?.(s, e)}
            onMouseLeave={() => onLeave?.()}
            draggable={!!onShiftMove}
            onDragStart={(e) => {
              e.dataTransfer.effectAllowed = "move";
              e.dataTransfer.setData("text/plain", "move-shift");
              setMovingId(s._id || s.id);
              onLeave?.();
            }}
            onDragEnd={() => {
              setMovingId(null);
              setDropDay(null);
            }}
            type="button"
          >
            <div className={css.evHead} style={{ ...headStyle, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
              <span>{meta.pill}</span>
              <ExcBadge exc={exc} />
            </div>
            <div className={css.evBody}>
              <div className={css.evMain}>
                {meta.facility ? (
                  <span className={css.evFac} style={{ color: color.ink }}>
                    <Icon name="building" size={11} />
                    {meta.facility}
                  </span>
                ) : null}
                <div className={css.evMid}>
                  {renderAv(meta, color)}
                  <span className={`${css.evName} ${meta.staffName ? "" : css.none}`}>{meta.title}</span>
                </div>
                {meta.clientName ? (
                  <span className={css.evPill} style={{ background: color.soft, color: color.ink, borderColor: color.line }}>
                    <Icon name="person" size={10} />
                    {meta.clientName}
                  </span>
                ) : null}
              </div>
              <span className={css.evTime}>
                {(s.allDay ? "All day" : fmtRange(s.startMin, s.endMin)) + (isPast ? " · Completed" : "")}
                {s.recurrence?.freq && s.recurrence.freq !== "none" ? <Icon name="repeat" size={11} className={css.recurIc} /> : null}
              </span>
            </div>
          </button>
        );
      });
    });
    const nowEl =
      isToday && nowMin >= sMin && nowMin < eMin ? (
        <div className={css.nowLine} style={{ top: ((nowMin - sMin) / 60) * HOUR_H }}>
          <span>{fmtTime(nowMin)}</span>
        </div>
      ) : null;
    const dragActive = dropArmed || movingId != null;
    const dropProps = dragActive
      ? {
          onDragOver: (e) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = movingId != null ? "move" : "copy";
            if (dropDay !== di) setDropDay(di);
          },
          onDragLeave: (e) => {
            // only clear when leaving the track itself, not entering a child
            if (!e.currentTarget.contains(e.relatedTarget)) setDropDay((p) => (p === di ? null : p));
          },
          onDrop: (e) => {
            e.preventDefault();
            setDropDay(null);
            if (movingId != null) {
              const s = items.find((x) => (x._id || x.id) === movingId);
              setMovingId(null);
              if (s) onShiftMove?.(s, d);
            } else if (dropArmed) {
              let payload = null;
              try {
                const raw = e.dataTransfer.getData("application/json");
                if (raw) payload = JSON.parse(raw);
              } catch (err) {
                /* ignore malformed payload */
              }
              onDayDrop?.(d, payload);
            }
          },
        }
      : {};
    return (
      <div
        key={d.toISOString()}
        className={`${css.track} ${isToday ? css.today : ""} ${isWeekend ? css.weekend : ""} ${
          dragActive ? css.trackDroppable : ""
        } ${dropDay === di ? css.trackDropHover : ""}`}
        style={{ height: trackH }}
        {...dropProps}
      >
        {lines}
        {nowEl}
        {events}
      </div>
    );
  });

  return (
    <div className={css.wkcalScroll}>
      <div className={css.wkcal}>
        <div className={css.heads}>
          <div className={css.corner} />
          {heads}
        </div>
        <div className={css.body}>
          <div className={css.gutter} style={{ height: trackH }}>{hours}</div>
          {tracks}
        </div>
      </div>
    </div>
  );
}
